import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui';
import { cn } from '@/lib/cn';
import type { DrawingPin } from '@/api/endpoints';

/**
 * 图纸下方的「本页图钉」清单。
 *
 * **这是「图钉内容看得见」这件事的主力**（CR-021）。
 *
 * 改之前图钉的说明挂在 `title` 属性上 —— 那是浏览器的悬停提示，
 * 手机上根本不存在悬停。工人在工地上打开图纸，看到的是一排橙点，
 * 点了还没有任何反应，等于这个功能不存在。
 *
 * 真正让人看到内容的不是「点击后弹出」，而是**默认就以文字列在那里**：
 * 不用点任何东西，就知道这一页标了三个位置、分别是什么、对应哪个活。
 * 点击只是进一步看详情和跳转。
 *
 * 放在 MediaViewer 外面而不是里面：查看器应该只管渲染图纸，
 * 不该知道角色、业务动作和路由。
 *
 * CR-023 之后图钉只关联隐蔽工程（「挂任务」随任务模块一起删了）。
 * 分包和 Manager **看得到**图钉内容，只是加不了、删不了。
 */
export function PinList({
  pins,
  page,
  selectedPinId,
  onSelect,
}: {
  /** 整张图纸的图钉，这里自己按页过滤 */
  pins: DrawingPin[];
  page: number;
  selectedPinId?: string | null;
  onSelect: (pin: DrawingPin) => void;
}) {
  const { t } = useTranslation();
  const pagePins = pins.filter((pin) => pin.page === page);

  if (pagePins.length === 0) return null;

  return (
    <section className="mt-3">
      <h3 className="mb-2 text-title font-semibold text-ink-2">
        {t('drawings.pinsOnPage', { count: pagePins.length })}
      </h3>
      <ul className="flex flex-col gap-2">
        {pagePins.map((pin, index) => (
          <li key={pin.id}>
            <button
              type="button"
              onClick={() => onSelect(pin)}
              // 工人端戴手套，整行都要点得中
              className={cn(
                'flex w-full min-h-row items-center gap-3 rounded-md border px-3 py-2 text-left',
                'transition-colors',
                pin.id === selectedPinId
                  ? 'border-brand-500 bg-brand-50'
                  : 'border-rule bg-surface hover:bg-surface-2',
              )}
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-safety-500 text-caption font-bold text-white">
                {index + 1}
              </span>

              <span className="min-w-0 flex-1">
                <span className="block truncate text-title">
                  {pin.label || t('drawings.pinUnnamed', { index: index + 1 })}
                </span>
                {/*
                  关联的隐蔽工程直接显示在行里 —— 工人不用点任何东西
                  就知道这个位置对应哪条记录，这是整条链路的意义所在
                */}
                {pin.hiddenWork && (
                  <span className="mt-0.5 block truncate text-caption text-ink-3">
                    {pin.hiddenWork.category} · {pin.hiddenWork.locationDesc}
                  </span>
                )}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * 图钉详情。
 *
 * 用 Card 而不是再造一个弹层：它就跟在清单下面展开，
 * 手机上不用处理遮罩、返回键、滚动锁这些事，也不会盖住图纸。
 */
export function PinDetail({
  pin,
  index,
  onRemove,
}: {
  pin: DrawingPin;
  index: number;
  /** 只有超管传这个：现场角色看得到内容，但删不了（CR-023） */
  onRemove?: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Card className="mt-3 p-4">
      <div className="flex items-start gap-3">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-600 text-caption font-bold text-white">
          {index}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-title font-semibold leading-snug">
            {pin.label || t('drawings.pinUnnamed', { index })}
          </h3>
          {pin.note && (
            <p className="mt-1.5 whitespace-pre-wrap text-body leading-relaxed text-ink-2">
              {pin.note}
            </p>
          )}
        </div>
      </div>

      {pin.hiddenWork && (
        <div className="mt-3 rounded-md border border-rule px-3 py-2">
          <span className="block text-caption text-ink-3">{t('nav.hidden')}</span>
          <span className="block text-body">
            {pin.hiddenWork.category} · {pin.hiddenWork.locationDesc}
          </span>
        </div>
      )}

      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="mt-3 min-h-touch text-body font-medium text-danger"
        >
          {t('drawings.removePin')}
        </button>
      )}
    </Card>
  );
}
